import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, UserPlus, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { supabase } from '@/lib/supabaseClient';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useRegisterCliente } from '@/components/hooks/useRegisterCliente';
import HeadSEO from '@/components/seo/HeadSEO';
import { logoutUser } from '@/components/utils/logoutUser';
import { openExternalLink } from '@/components/utils/openExternalLink';
import { passwordValida } from '@/components/utils/password';
import PasswordRequisitos from '@/components/ui/PasswordRequisitos';

export default function RegisterClient() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    register,
    loading,
    reset,
    checkEmail,
    emailExists,
    tienePerfilCliente,
  } = useRegisterCliente();

  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const loadSession = async () => {
      const { data } = await supabase.auth.getSession();
      const user = data.session?.user;
      if (!user) return;
      setIsGoogleUser(true);
      setEmail(user.email ?? '');
      const fullName =
        user.user_metadata?.full_name || user.user_metadata?.name;
      if (fullName) setNombre(fullName);
    };
    loadSession();
  }, []);

  const isFormValid = useMemo(() => {
    if (!nombre.trim() || !telefono.trim() || !consent) return false;
    if (!isGoogleUser) {
      if (!email.trim() || !passwordValida(password)) return false;
      if (!emailExists && password !== confirmPassword) return false;
    }
    return true;
  }, [
    nombre,
    telefono,
    consent,
    isGoogleUser,
    email,
    password,
    confirmPassword,
    emailExists,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    if (!isGoogleUser) {
      // Doble comprobación: el botón puede haberse habilitado antes del último render
      if (!passwordValida(password)) {
        toast.error(t('password.errorWeak'));
        return;
      }
      await checkEmail(email);
      if (tienePerfilCliente) return;
    }
    const result = await register(
      { email, nombre, telefono, password, confirmPassword },
      consent,
      isGoogleUser
    );
    if (!result.ok) return;
    toast.success(t('registerClient.successToast'));
    reset();
    const returnTo =
      localStorage.getItem('after_register_return_to') || '/panel-cliente';
    localStorage.removeItem('after_register_return_to');
    localStorage.removeItem('auth_origin');
    navigate(returnTo, { replace: true });
  };

  const handleBack = async () => {
    const origin = localStorage.getItem('auth_origin'); // leer antes
    if (isGoogleUser) {
      await logoutUser(); // ahora ya limpia auth_origin también
    } else {
      localStorage.removeItem('auth_origin'); // si no es Google, límpialo igual
    }
    navigate(origin || '/', { replace: true });
  };

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-black">
      {/* ── SEO ───────────────────────────────────────────────────────────── */}
      <HeadSEO
        title="Crear cuenta como cliente — Bookoll"
        description="Regístrate gratis en Bookoll como cliente y reserva citas en peluquerías, centros de estética y salones de belleza cerca de ti."
        canonical="https://bookoll.com/register-client"
      />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative z-0 overflow-hidden text-white w-full">
        {/* Fondo modo claro */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse at 15% 60%, rgba(40,142,127,0.22) 0%, transparent 50%),
              radial-gradient(ellipse at 85% 20%, rgba(16,50,84,0.5) 0%, transparent 55%),
              linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)
            `,
          }}
        />
        {/* Fondo modo oscuro: negro con resplandor teal, igual que Login */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 55%, rgba(40, 142, 127, 0.35) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 15%, rgba(16, 50, 84, 0.45) 0%, transparent 55%),
              linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
            `,
          }}
        />

        {/* Orb izquierda */}
        <motion.div
          className="absolute top-10 left-[5%] w-56 h-56 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(40,142,127,0.18) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Orb derecha */}
        <motion.div
          className="absolute top-4 right-[8%] w-64 h-64 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.75, 0.4] }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />

        {/* Botón volver — respeta el notch/isla dinámica en móvil */}
        <button
          type="button"
          onClick={handleBack}
          aria-label="Volver atrás"
          className="absolute left-5 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white hover:bg-white/20 transition-colors"
          style={{ top: 'calc(env(safe-area-inset-top) + 1.25rem)' }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline font-medium">Volver</span>
        </button>

        {/* Contenido hero */}
        <div className="relative z-10 pt-20 pb-28 px-6 flex flex-col items-center text-center max-w-3xl mx-auto">
          <motion.div
            className="mb-5 w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <UserPlus className="w-7 h-7 text-white" />
          </motion.div>

          <motion.h1
            className="text-3xl md:text-4xl font-bold tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          >
            {t('registerClient.title')}
          </motion.h1>

          <motion.div
            className="mt-4 h-px w-16 mx-auto"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
          />

          <motion.p
            className="mt-3 text-sm text-white/70 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
          >
            {t('registerClient.subtitle')}
          </motion.p>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
          <svg
            viewBox="0 0 1440 60"
            preserveAspectRatio="none"
            className="w-full h-10 sm:h-14"
          >
            <path
              d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z"
              className="fill-white dark:fill-black"
            />
          </svg>
        </div>
      </section>

      {/* ── Card ──────────────────────────────────────────────────────────── */}
      <div className="relative z-10 flex justify-center px-4 -mt-10 pb-16">
        <motion.form
          onSubmit={handleSubmit}
          className="w-full max-w-lg bg-white dark:bg-[#1c1c1e] rounded-lg shadow-2xl border sm:border-2 border-gray-200 dark:border-white/10 p-5 sm:p-8 flex flex-col"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Campos */}
          <div className="space-y-5 flex-1">
            <div>
              <Label className="dark:text-white/90">{t('registerClient.email')}</Label>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                disabled={isGoogleUser}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => {
                  if (!isGoogleUser) checkEmail(email);
                }}
                className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
            </div>

            <div>
              <Label className="dark:text-white/90">{t('registerClient.fullName')}</Label>
              <Input
                autoComplete="name"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
            </div>

            <div>
              <Label className="dark:text-white/90">{t('registerClient.phone')}</Label>
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                className="mt-1 h-11 dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
            </div>

            {!isGoogleUser && (
              <>
                <div>
                  <Label className="dark:text-white/90">{t('registerClient.password')}</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('password.placeholder')}
                      className="h-11 pr-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <PasswordRequisitos password={password} />
                </div>

                <div>
                  <Label className="dark:text-white/90">{t('registerClient.confirmPassword')}</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-11 pr-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      tabIndex={-1}
                      aria-label={
                        showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && password !== confirmPassword && (
                    <p className="mt-2 text-xs text-red-500">
                      {t('password.errorMatch')}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Consentimiento */}
          <div className="flex items-start gap-3 p-4 mt-6 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
            <Switch checked={consent} onCheckedChange={setConsent} />
            <Label className="text-xs leading-relaxed text-gray-500 dark:text-white/50">
              {t('registerClient.consent')}{' '}
              <button
                type="button"
                onClick={() => openExternalLink('https://bookoll.com/privacy-policy')}
                className="underline text-[#103254] dark:text-[#4fb8a3] font-medium text-xs leading-relaxed"
              >
                {t('registerClient.privacyPolicy')}
              </button>
            </Label>
          </div>

          {/* Submit: sticky al fondo DE LA TARJETA (no de toda la pantalla),
              para que se sienta parte del formulario y no una barra suelta.
              Se queda visible mientras hay tarjeta debajo. */}
          <div
            className="sticky bottom-0 -mx-5 sm:-mx-8 -mb-5 sm:-mb-8 mt-5 px-5 sm:px-8 py-4 bg-white/95 dark:bg-[#1c1c1e]/95 backdrop-blur-sm border-t border-gray-100 dark:border-white/10 rounded-b-lg"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          >
            <Button
              type="submit"
              disabled={!isFormValid || loading}
              className="w-full h-12 sm:h-11 text-base sm:text-sm bg-[#103254] dark:bg-[#288E7F] text-white hover:bg-[#1a4a6e] dark:hover:bg-[#1e7a6e] active:scale-[0.98] transition-transform disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed disabled:active:scale-100 disabled:hover:bg-[#103254] dark:disabled:hover:bg-[#288E7F]"
            >
              {loading
                ? t('registerClient.registering')
                : t('registerClient.register')}
            </Button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}